<script setup>
import { ref, watch } from "vue";
import { Link } from "@inertiajs/vue3";
import BrandLogo from "@/Components/Brand/BrandLogo.vue";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import BrandMobileNav from "./BrandMobileNav.vue";

const mobileNavOpen = ref(false);

// The logo + hamburger only lift above the z-50 layer while the drawer is
// showing (through the end of its slide-out), so modalogs, which share z-50,
// cover the header like the rest of the page (BH-68).
const mobileNavLifted = ref(false);
watch(mobileNavOpen, (open) => {
    if (open) {
        mobileNavLifted.value = true;
    }
});

const navLinks = [
    { label: "Create a Wishlist", route: "register" },
    { label: "Find Friends", route: "friends.find-friends" },
    { label: "Gift Ideas", route: "explore" },
];
</script>

<template>
    <header class="px-5 pt-5">
        <div class="mx-auto max-w-[1472px] rounded-t-brand bg-white">
            <nav
                class="flex items-center justify-between gap-4 px-5 py-5 sm:px-9 sm:py-6"
            >
                <!--
                    Compact lockup below sm so the wordmark and the 52px
                    hamburger fit a 360px viewport on one line. Two instances
                    (not bare `hidden`): the legacy .hidden{display:none
                    !important} rule would keep a `hidden sm:flex` pair hidden
                    at every width on app.scss pages.
                -->
                <!--
                    The logo and hamburger stay put when the mobile drawer
                    opens: z-[60] lifts them above the fixed z-50 panel while
                    it is showing, and they only change state — the logo
                    flips to its inverse colours, the bars morph into a ✕.
                -->
                <Link
                    :href="route('home')"
                    class="relative shrink-0"
                    :class="{ 'z-[60]': mobileNavLifted }"
                >
                    <BrandLogo
                        :variant="mobileNavOpen ? 'inverse' : 'dark'"
                        class="max-sm:hidden"
                    />
                    <BrandLogo
                        :variant="mobileNavOpen ? 'inverse' : 'dark'"
                        compact
                        class="sm:hidden"
                    />
                </Link>

                <ul
                    class="items-center gap-9 font-brand-display text-[17px] font-semibold uppercase tracking-wide text-[#102a3d] max-lg:hidden lg:flex xl:gap-[54px]"
                >
                    <li v-for="link in navLinks" :key="link.label">
                        <!--
                            Explicit color: legacy `a { color: … }` beats
                            inheritance on pages that load app.scss.
                        -->
                        <a
                            :href="route(link.route)"
                            class="text-[#102a3d] hover:text-brand-red"
                        >
                            {{ link.label }}
                        </a>
                    </li>
                </ul>

                <div class="items-center gap-6 max-lg:hidden lg:flex">
                    <a
                        :href="route('login')"
                        class="font-brand text-[15px] font-bold text-brand-navy underline underline-offset-2"
                    >
                        Member Log In
                    </a>
                    <BrandButton
                        :href="route('register')"
                        class="px-7 py-3.5 text-[15px]"
                    >
                        Create Account
                    </BrandButton>
                </div>

                <button
                    type="button"
                    class="relative flex size-[52px] shrink-0 appearance-none flex-col items-center justify-center gap-1.5 rounded-brand border border-solid transition-colors duration-300 lg:hidden"
                    :class="[
                        mobileNavOpen
                            ? 'border-[#dad9d2]/20'
                            : 'border-[#dad9d2]',
                        { 'z-[60]': mobileNavLifted },
                    ]"
                    :aria-label="
                        mobileNavOpen ? 'Close navigation' : 'Open navigation'
                    "
                    aria-controls="brand-mobile-nav"
                    :aria-expanded="mobileNavOpen"
                    @click="mobileNavOpen = !mobileNavOpen"
                >
                    <span
                        class="block h-0.5 w-7 transition-all duration-300"
                        :class="
                            mobileNavOpen
                                ? 'translate-y-[8px] rotate-45 bg-white'
                                : 'bg-brand-navy'
                        "
                    ></span>
                    <span
                        class="block h-0.5 w-7 transition-all duration-300"
                        :class="
                            mobileNavOpen
                                ? 'opacity-0 bg-white'
                                : 'bg-brand-navy'
                        "
                    ></span>
                    <span
                        class="block h-0.5 w-7 transition-all duration-300"
                        :class="
                            mobileNavOpen
                                ? '-translate-y-[8px] -rotate-45 bg-white'
                                : 'bg-brand-navy'
                        "
                    ></span>
                </button>
            </nav>
        </div>

        <BrandMobileNav
            :open="mobileNavOpen"
            @close="mobileNavOpen = false"
            @closed="mobileNavLifted = false"
        />
    </header>
</template>
